import React, { useState } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Select, MenuItem, FormControl, InputLabel,
    CircularProgress, Alert, Box, Typography, LinearProgress
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";

export default function ExtractFactsModal({ open, onClose, onComplete }) {
    const { chatbotId } = useParams();
    const [sourceType, setSourceType] = useState('all');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    const handleExtract = async () => {
        setLoading(true);
        setError('');
        setResult(null);

        try {
            const response = await axiosInstance.post('/api/facts/extract', {
                source_type: sourceType
            }, {
                params: { chatbot_id: chatbotId },
                timeout: 300000 // 5 min timeout for LLM extraction
            });

            if (response.data.success) {
                setResult(response.data);
                onComplete();
            } else {
                setError(response.data.message || 'Extraction failed.');
            }
        } catch (err) {
            if (err.code === 'ECONNABORTED') {
                setError('Extraction timed out. Try selecting a specific source type instead of "All Sources".');
            } else {
                setError(err.response?.data?.message || err.message || 'An error occurred during extraction.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setResult(null);
        setError('');
        setSourceType('all');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AutoFixHighIcon color="primary" />
                Extract Facts from Sources
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
                    Use AI to automatically extract entities, facts, and relationships from your existing data sources.
                    The system will analyze your content and populate the fact table.
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Source Type</InputLabel>
                    <Select
                        value={sourceType}
                        onChange={(e) => setSourceType(e.target.value)}
                        label="Source Type"
                        disabled={loading}
                    >
                        <MenuItem value="all">All Sources</MenuItem>
                        <MenuItem value="document">Documents Only</MenuItem>
                        <MenuItem value="website">Websites Only</MenuItem>
                        <MenuItem value="image">Images Only</MenuItem>
                        <MenuItem value="voice">Voice/Audio Only</MenuItem>
                    </Select>
                </FormControl>

                {loading && (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                        <CircularProgress size={40} sx={{ mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                            Analyzing sources and extracting facts...
                        </Typography>
                        <LinearProgress sx={{ mt: 2 }} />
                    </Box>
                )}

                {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

                {result && (
                    <Alert severity="success" sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Extraction Complete</Typography>
                        <Typography variant="body2">
                            Facts created: <strong>{result.facts_created}</strong><br />
                            Relationships created: <strong>{result.relationships_created}</strong><br />
                            Conflicts resolved: <strong>{result.conflicts_resolved}</strong><br />
                            Sources processed: <strong>{result.sources_processed}</strong>
                        </Typography>
                        {result.errors && result.errors.length > 0 && (
                            <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                                {result.errors.length} source(s) had errors during processing.
                            </Typography>
                        )}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleClose}>{result ? 'Close' : 'Cancel'}</Button>
                {!result && (
                    <Button
                        onClick={handleExtract}
                        variant="contained"
                        disabled={loading}
                        startIcon={<AutoFixHighIcon />}
                    >
                        {loading ? 'Extracting...' : 'Extract Facts'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
